/* ============================================================
   <sw-header> — owns state for the SW Header section
   ============================================================
   State classes (toggled on the <sw-header> element):
     sw-header--scrolled    window scroll > threshold
     sw-header--hidden      scrolling down, above threshold (bars translate up)
     sw-header--expanded    any overlay open: drawer, mega, search, cart
     sw-header--transparent merchant opt-in from Liquid

   Public data-attributes on buttons:
     data-sw-search-trigger   opens <search-modal>
     data-sw-cart-trigger     opens <cart-drawer>
     data-sw-drawer-toggle    toggles mobile drawer
     data-sw-mega-trigger="<id>"  opens mega panel with matching [data-sw-mega]

   Theme-editor safety:
     Shopify's theme editor re-renders this section on every setting change,
     which disconnects and reconnects the custom element. All global listeners
     (window scroll, document keydown/click) and the body MutationObserver
     MUST be removed in disconnectedCallback — otherwise they accumulate
     each edit, stack up on body-class mutations, and can cause the admin
     to thrash and re-render in a loop.
   ============================================================ */

class SwHeader extends HTMLElement {
  constructor() {
    super();
    // Bind once so the same function reference is used for add/remove listener.
    this.onScroll = this.onScroll.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this._onScrollEvent = this._onScrollEvent.bind(this);
    this._onSearchClose = this._onSearchClose.bind(this);
    this._onCartOpen = this._onCartOpen.bind(this);
  }

  connectedCallback() {
    this.threshold = 60;
    this.lastScroll = window.pageYOffset || 0;
    this.ticking = false;
    this.megaOpenTimer = null;
    this.megaCloseTimer = null;
    this.megaOpenDelay = 120;
    this.megaCloseDelay = 150;
    this.currentMega = null;
    this.overlays = new Set(); // tracks what's open: 'drawer'|'mega'|'search'|'cart'
    this._cartWasOpen = false;
    this._searchWasOpen = false;

    // Cache the Shopify section wrapper — it's the stable reference for
    // overlay positioning. Only `.sw-header__bars` gets transformed for
    // hide/show; the wrapper itself stays put, so its bottom edge always
    // reflects where overlays should pin even mid-animation.
    this._sectionWrapper = this.closest('[id^="shopify-section-"]');

    this.bindTriggers();
    this.bindMegaPanels();
    this.bindOverlayEvents();

    window.addEventListener('scroll', this._onScrollEvent, { passive: true });
    window.addEventListener('resize', this._onScrollEvent, { passive: true });
    this.onScroll();
    this._updateOverlayTop();

    document.addEventListener('keydown', this.onKey);
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('searchmodal:close', this._onSearchClose);
    document.addEventListener('cartdrawer:opened', this._onCartOpen);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScrollEvent);
    window.removeEventListener('resize', this._onScrollEvent);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('searchmodal:close', this._onSearchClose);
    document.removeEventListener('cartdrawer:opened', this._onCartOpen);

    if (this._bodyObserver) {
      this._bodyObserver.disconnect();
      this._bodyObserver = null;
    }
    clearTimeout(this.megaOpenTimer);
    clearTimeout(this.megaCloseTimer);
  }

  /* -------- scroll hide/show --------
     Direction-based reveal: scroll down past threshold → hide;
     scroll up by more than `delta` → reveal. The `delta` deadzone avoids
     flickering from inertial scroll micro-oscillations, which would otherwise
     leave the header stuck in the hidden state mid-page. */
  _onScrollEvent() {
    if (this.ticking) return;
    window.requestAnimationFrame(this.onScroll);
    this.ticking = true;
  }

  onScroll() {
    const y = Math.max(
      window.pageYOffset || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0,
      0
    );
    const delta = y - this.lastScroll;
    const scrolled = y > this.threshold;
    this.classList.toggle('sw-header--scrolled', scrolled);

    // While any overlay is open, never hide the bar.
    if (this.classList.contains('sw-header--expanded')) {
      this.classList.remove('sw-header--hidden');
      this.lastScroll = y;
      this.ticking = false;
      return;
    }

    // Always reveal near the top.
    if (y <= this.threshold) {
      this.classList.remove('sw-header--hidden');
    } else if (delta > 4) {
      this.classList.add('sw-header--hidden');
    } else if (delta < -4) {
      this.classList.remove('sw-header--hidden');
    }

    this._updateOverlayTop();

    this.lastScroll = y;
    this.ticking = false;
  }

  /* Sync the --sw-header-overlay-top CSS variable with the actual bottom
     edge of the sticky header section. Drawer + search modal use this so
     they pin flush below the bars regardless of whether the (non-sticky)
     announcement bar is still in view. The hardcoded `+ 36px` it replaced
     was correct only at the top of the page. */
  _updateOverlayTop() {
    const ref = this._sectionWrapper || this;
    const rect = ref.getBoundingClientRect();
    this.style.setProperty('--sw-header-overlay-top', Math.max(0, rect.bottom) + 'px');
  }

  /* -------- expanded-state management -------- */
  setOverlay(name, open) {
    if (open) {
      this.overlays.add(name);
      // Always reveal the bars when an overlay opens. Without this, an
      // overlay opened while the bars were mid-hide leaves the drawer
      // sitting under a translated header — visually broken.
      this.classList.remove('sw-header--hidden');
      this._updateOverlayTop();
    } else {
      this.overlays.delete(name);
    }
    this.classList.toggle('sw-header--expanded', this.overlays.size > 0);
  }

  /* Close any other open overlay before opening a new one. The trigger
     handlers stopPropagation so the search modal's own body-click closer
     never fires; without this, clicking cart while search is open (or the
     reverse) leaves both panels stacked on screen. */
  _closeOtherOverlays(keep) {
    if (keep !== 'search') {
      const searchModal = this.querySelector('search-modal');
      if (searchModal && typeof searchModal.close === 'function' &&
          document.body.classList.contains('search-modal--open')) {
        searchModal.close();
      }
    }
    if (keep !== 'cart') {
      const cart = this.querySelector('cart-drawer');
      if (cart && typeof cart.closeMenuDrawer === 'function' &&
          (document.body.classList.contains('mini-cart--open') ||
           document.body.classList.contains('mini-cart--opening'))) {
        cart.closeMenuDrawer();
      }
    }
    if (keep !== 'drawer') {
      const toggle = this.querySelector('[data-sw-drawer-toggle][aria-expanded="true"]');
      if (toggle) toggle.click();
    }
    if (keep !== 'mega' && this._closeMega) this._closeMega();
  }

  /* -------- triggers (search, cart, drawer) -------- */
  bindTriggers() {
    this.querySelectorAll('[data-sw-search-trigger]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._closeOtherOverlays('search');
        const summary = this.querySelector('[data-sw-search-summary]');
        if (summary) summary.click();
      });
    });

    this.querySelectorAll('[data-sw-cart-trigger]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._closeOtherOverlays('cart');
        const cart = this.querySelector('cart-drawer');
        if (cart && typeof cart.openMenuDrawer === 'function') {
          cart.openMenuDrawer();
        } else {
          window.location.href = btn.getAttribute('data-cart-url') || '/cart';
        }
      });
    });

    // Close-on-overlay-click — the overlay sits inside <cart-drawer>, so
    // MenuDrawer.onBodyClick (which closes only when the click is OUTSIDE
    // the drawer) won't fire. Wire it explicitly here.
    this.querySelectorAll('[data-sw-cart-overlay]').forEach((overlay) => {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        const cart = this.querySelector('cart-drawer');
        if (cart && typeof cart.closeMenuDrawer === 'function') {
          cart.closeMenuDrawer(e);
        }
      });
    });

    const toggle = this.querySelector('[data-sw-drawer-toggle]');
    const drawer = this.querySelector('[data-sw-drawer]');
    if (toggle && drawer) {
      toggle.addEventListener('click', () => {
        const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
        if (willOpen) this._closeOtherOverlays('drawer');
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        toggle.setAttribute('aria-label', willOpen ? 'Close menu' : 'Menu');
        drawer.dataset.open = willOpen ? 'true' : 'false';
        if (willOpen) drawer.removeAttribute('hidden'); else drawer.setAttribute('hidden', '');
        document.documentElement.classList.toggle('sw-header--drawer-open', willOpen);
        this.setOverlay('drawer', willOpen);
      });
    }
  }

  /* -------- mega menu hover+click -------- */
  bindMegaPanels() {
    const items = this.querySelectorAll('[data-sw-mega-trigger]');
    const panels = this.querySelectorAll('[data-sw-mega]');

    const openMega = (id) => {
      clearTimeout(this.megaCloseTimer);
      if (this.currentMega === id) return;
      panels.forEach((p) => {
        const match = p.dataset.swMega === id;
        p.dataset.open = match ? 'true' : 'false';
        if (match) p.removeAttribute('hidden'); else p.setAttribute('hidden', '');
      });
      items.forEach((t) => {
        t.setAttribute('aria-expanded', t.dataset.swMegaTrigger === id ? 'true' : 'false');
      });
      this.currentMega = id;
      this.setOverlay('mega', true);
    };

    const closeMega = () => {
      panels.forEach((p) => {
        p.dataset.open = 'false';
        p.setAttribute('hidden', '');
      });
      items.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.currentMega = null;
      this.setOverlay('mega', false);
    };

    items.forEach((t) => {
      t.addEventListener('mouseenter', () => {
        clearTimeout(this.megaCloseTimer);
        clearTimeout(this.megaOpenTimer);
        this.megaOpenTimer = setTimeout(() => openMega(t.dataset.swMegaTrigger), this.megaOpenDelay);
      });
      t.addEventListener('mouseleave', () => {
        clearTimeout(this.megaOpenTimer);
        this.megaCloseTimer = setTimeout(closeMega, this.megaCloseDelay);
      });
      t.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.currentMega === t.dataset.swMegaTrigger) closeMega();
        else openMega(t.dataset.swMegaTrigger);
      });
      t.addEventListener('focus', () => openMega(t.dataset.swMegaTrigger));
    });

    panels.forEach((p) => {
      p.addEventListener('mouseenter', () => clearTimeout(this.megaCloseTimer));
      p.addEventListener('mouseleave', () => {
        clearTimeout(this.megaOpenTimer);
        this.megaCloseTimer = setTimeout(closeMega, this.megaCloseDelay);
      });
    });

    this._closeMega = closeMega;
  }

  /* -------- overlay open/close events from theme JS -------- */
  _onSearchClose() { this.setOverlay('search', false); }
  _onCartOpen() { this.setOverlay('cart', true); }

  bindOverlayEvents() {
    // Body class observer: the theme toggles body classes for mini-cart +
    // search-modal. Narrowly scoped to `class` changes on <body>; ignores
    // edits the Shopify theme admin makes to unrelated attributes.
    const body = document.body;
    const scanBody = () => {
      if (!this.isConnected) return;
      const cartOpen = body.classList.contains('mini-cart--open') || body.classList.contains('mini-cart--opening');
      const searchOpen = body.classList.contains('search-modal--open');
      if (cartOpen !== this._cartWasOpen) { this.setOverlay('cart', cartOpen); this._cartWasOpen = cartOpen; }
      if (searchOpen !== this._searchWasOpen) { this.setOverlay('search', searchOpen); this._searchWasOpen = searchOpen; }
    };
    this._bodyObserver = new MutationObserver(scanBody);
    this._bodyObserver.observe(body, { attributes: true, attributeFilter: ['class'] });
  }

  onKey(e) {
    if (e.key !== 'Escape') return;
    if (this.currentMega) this._closeMega();
    const toggle = this.querySelector('[data-sw-drawer-toggle][aria-expanded="true"]');
    if (toggle) toggle.click();
  }

  onDocClick(e) {
    if (!this.currentMega) return;
    if (!this.contains(e.target)) this._closeMega();
  }
}

if (!customElements.get('sw-header')) {
  customElements.define('sw-header', SwHeader);
}
